import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import MainLayout from './components/layout/MainLayout';
import GlobalLoadingBar from './components/ui/GlobalLoadingBar';
import { ResearchJobsNotifier } from './components/ResearchJobsNotifier';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminRoute from './components/auth/AdminRoute';
import { useAppStore } from './stores/appStore';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import CaseLibrary from './pages/CaseLibrary';
import NewCase from './pages/NewCase';
import CaseDetail from './pages/CaseDetail';
import CaseSummary from './pages/CaseSummary';
import ProcessingPhase from './pages/ProcessingPhase';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import Admin from './pages/Admin';
import AdminCases from './pages/AdminCases';
import AIAssistantPage from './pages/AIAssistantPage';
import ChatbotPage from './pages/ChatbotPage';
import CompanyResearch from './pages/CompanyResearch';
import NewCompanyResearch from './pages/NewCompanyResearch';
import CompanyDetail from './pages/CompanyDetail';
import CaseClientDetails from './pages/CaseClientDetails';
import DomainPackLibrary from './pages/DomainPackLibrary';
import GenerateDomainPack from './pages/GenerateDomainPack';
import DomainPackProgress from './pages/DomainPackProgress';
import DomainPackDetail from './pages/DomainPackDetail';
import ResearchLibraryAdmin from './pages/ResearchLibraryAdmin';
import EnterpriseSystemsPage from './pages/EnterpriseSystemsPage';
import EnterpriseSystemDetailPage from './pages/EnterpriseSystemDetailPage';
import CreateEnterpriseSystemPage from './pages/CreateEnterpriseSystemPage';
import IndustryLibrary from './pages/IndustryLibrary';
import IndustryDetail from './pages/IndustryDetail';
import GenerateIndustryResearch from './pages/GenerateIndustryResearch';
import CaseDiscovery from './pages/CaseDiscovery';
import NewCaseWizard from './pages/NewCaseWizard';
import AnalysisWizard from './pages/AnalysisWizard';
import DesignWizard from './pages/DesignWizard';
import ResearchQueue from './pages/ResearchQueue';
import ResearchCompanyPage from './pages/ResearchCompanyPage';
import AgentBuilderPage from './pages/agent-builder/AgentBuilderPage';

/**
 * Agent Builder cockpit (Phase 2) — ADDITIVE & FLAG-GATED. The route below is
 * registered ONLY when VITE_ENABLE_AGENT_BUILDER is truthy (mirrors the backend
 * `enable_agent_builder` flag). With the flag OFF the route is absent and the app
 * behaves byte-identically to before.
 */
const AGENT_BUILDER_ENABLED =
  String(import.meta.env.VITE_ENABLE_AGENT_BUILDER ?? '').toLowerCase() === 'true';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
      // Prevent the same endpoints from being refetched every time the tab/window
      // regains focus or the network reconnects. Combined with staleTime this
      // stops the "same API called 3+ times on every page load" behaviour.
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  },
});

function App() {
  const initializeAuth = useAppStore((state) => state.initializeAuth);

  // Restore the persisted session on startup by decoding the stored JWT (this
  // backend has no /auth/me). Runs synchronously enough to settle before the
  // first paint of a protected route; an expired token clears the session.
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  return (
    <QueryClientProvider client={queryClient}>
      <GlobalLoadingBar />
      <BrowserRouter>
        <ResearchJobsNotifier />
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />

          {/* Protected Routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <>
                  <MainLayout />
                </>
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="case-library" element={<CaseLibrary />} />
            <Route path="chat" element={<AIAssistantPage />} />
            <Route path="graph-assistant" element={<ChatbotPage />} />
            <Route path="cases/new-wizard" element={<NewCaseWizard />} />
            <Route path="cases/analysis-wizard" element={<AnalysisWizard />} />
            <Route path="cases/design-wizard" element={<DesignWizard />} />
            <Route path="cases/new" element={<Navigate to="/cases/new-wizard" replace />} />
            <Route path="cases/:caseId/edit" element={<NewCase />} />
            <Route path="cases/:caseId/summary" element={<CaseSummary />} />
            <Route path="cases/:caseId/client-details" element={<CaseClientDetails />} />
            <Route path="cases/:caseId/discovery" element={<CaseDiscovery />} />
            <Route path="cases/:caseId/processing" element={<ProcessingPhase />} />
            <Route path="cases/:caseId/detail" element={<CaseDetail />} />
            <Route path="companies" element={<CompanyResearch />} />
            <Route path="companies/research" element={<ResearchCompanyPage />} />
            <Route path="companies/new" element={<NewCompanyResearch />} />
            <Route path="companies/:companyId" element={<CompanyDetail />} />
            <Route path="domain-packs" element={<DomainPackLibrary />} />
            <Route path="domain-packs/generate" element={<GenerateDomainPack />} />
            <Route path="domain-packs/admin" element={<ResearchLibraryAdmin />} />
            <Route path="domain-packs/:domainPackId/progress" element={<DomainPackProgress />} />
            <Route path="domain-packs/:domainPackId" element={<DomainPackDetail />} />
            <Route path="enterprise-systems" element={<EnterpriseSystemsPage />} />
            <Route path="enterprise-systems/new" element={<CreateEnterpriseSystemPage />} />
            <Route path="enterprise-systems/:systemId/edit" element={<CreateEnterpriseSystemPage />} />
            <Route path="enterprise-systems/:systemId" element={<EnterpriseSystemDetailPage />} />
            <Route path="industries" element={<IndustryLibrary />} />
            <Route path="industries/generate" element={<GenerateIndustryResearch />} />
            <Route path="industries/:industryId" element={<IndustryDetail />} />
            <Route path="profile" element={<Profile />} />
            <Route path="settings" element={<Settings />} />
            <Route
              path="admin"
              element={
                <AdminRoute>
                  <Admin />
                </AdminRoute>
              }
            />
            <Route
              path="admin/cases"
              element={
                <AdminRoute>
                  <AdminCases />
                </AdminRoute>
              }
            />
            <Route path="research-queue" element={<ResearchQueue />} />
            {AGENT_BUILDER_ENABLED && (
              <Route path="agent-builder" element={<AgentBuilderPage />} />
            )}
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </QueryClientProvider>
  );
}

export default App;
